import {
    Box,
    IconButton,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
    Menu,
    MenuItem,
    Divider,
    Radio,
    Checkbox,
    Button
} from "@mui/material";

import { Add, Cancel, Delete, MoreVert } from "@mui/icons-material";
import { memo, useEffect, useState } from "react";
import ResourcesView from "../../../components/resources";
import { ObjectiveQuestion } from "../../../../../models/assessment";
import StarIcon from '../../assets/icons/star.png';
import RichTextEditor from "../../../components/rich-text/rich-text-editor";

interface ObjectiveViewProps {
    item: ObjectiveQuestion;
    setSaved: React.Dispatch<React.SetStateAction<boolean>>;
    register: (id: string, getItem: () => ObjectiveQuestion) => void;
    type: number;
    auto_marker: boolean;
    saveFunction: () => void;
}
  
function ObjectiveView({
    item,
    setSaved,
    register,
    type,
    auto_marker,
    saveFunction
}: ObjectiveViewProps) {

    const [localItem, setLocalItem] = useState<ObjectiveQuestion>(item);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [resourceAnchorEl, setResourceAnchorEl] = useState<null | HTMLElement>(null);
    const [resourcesVisible, setResourcesVisible] = useState<boolean>(false);
    const [descriptionVisible, setDescriptionVisible] = useState<boolean>(false);
    const [editing, setEditing] = useState<boolean>(false);
  
    useEffect(() => {
        register(item.id, () => localItem);
    }, [item.id, localItem, register]);
  
  
    const handleChange = (field: keyof ObjectiveQuestion, value: any) => {
        setSaved(false);
        setLocalItem((prev) => ({ ...prev, [field]: value }));
        saveFunction();
    };
  
    const handleOptionChange = (value: string, i: number) => {
        const updated = [...(localItem.options || [])];
        const oldValue = updated[i];
        updated[i] = value;
        handleChange("options", updated);
        
        if (Array.isArray(localItem.correct_answer) && localItem.correct_answer.includes(oldValue)) {
            const updatedCorrectAnswers = [...localItem.correct_answer];
            const index = updatedCorrectAnswers.indexOf(oldValue);
            if (index !== -1) {
                updatedCorrectAnswers[index] = value;
                handleChange("correct_answer", updatedCorrectAnswers);
            }
        }
    };
  
    const addOption = () => {
        const updated = [...(localItem.options), `Option ${localItem.options.length + 1}`];
        handleChange("options", updated);
    };
  
    const removeOption = (i: number) => {
        const option = localItem.options?.[i] || "";
        const updated = (localItem.options || []).filter((_, idx) => idx !== i);
        handleChange("options", updated);
      
        if (Array.isArray(localItem.correct_answer) && localItem.correct_answer.includes(option)) {
            const updatedCorrectAnswers = localItem.correct_answer.filter(
                answer => answer !== option
            );
            handleChange("correct_answer", updatedCorrectAnswers);
        }
    };
  
    const toggleRequired = () => {
        handleChange("required", !localItem.required);
    };
  
    const handleCorrectAnswerSelect = (option: string) => {
      const currentAnswers = [...localItem.correct_answer];
        
      if (type === 3) {
            handleChange("correct_answer", [option]);
      } else {
            if (currentAnswers.includes(option)) {
                handleChange(
                    "correct_answer", 
                    currentAnswers.filter(answer => answer !== option)
                );
            } else {
                handleChange("correct_answer", [...currentAnswers, option]);
            }
      }
    };
  
    const isOptionCorrect = (option: string): boolean => {
        return localItem.correct_answer.includes(option);
    };
  
    const OptionIcon = type === 3 ? Radio : Checkbox;
  
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
  
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const toggleResourcesVisibility = () => {
        setResourcesVisible(!resourcesVisible);
        handleMenuClose();
    };
  
    const toggleDescriptionVisibility = () => {
        setDescriptionVisible(!descriptionVisible);
        handleMenuClose();
    };
  
    const toggleShuffleOptions = () => {
        handleChange("shuffle_options", !localItem.shuffle_options);
        handleMenuClose();
    };
  
    const handleImageClick = () => {
      setEditing(true);
    };
  
    const handleDoneClick = () => {
      setEditing(false);
    };
  
    return (
        <Paper
            sx={{
                backgroundColor: "#fff",
                padding: 2,
                display: "flex",
                flexDirection: "column",
                transition: "all 0.3s ease",
                gap: 2
            }}
        >
            {!editing ? (
                <>
                    <RichTextEditor
                        id={localItem.id}
                        type="title"
                        value={localItem.title}
                        onChange={(val) => handleChange("title", val)}
                        fullBorder={false}
                        userPlaceholder={item.type === 3 ? "Untitled Radio Question" : "Untitled Checkbox Question"}
                    />
                    

                    {descriptionVisible && (
                        <RichTextEditor
                            id={localItem.id}
                            type="description"
                            value={localItem.description}
                            onChange={(val) => handleChange("description", val)}
                        />
                    )}
        
                    {resourcesVisible && (
                        <>
                            <Box
                                onClick={(event: React.MouseEvent<HTMLElement>) => setResourceAnchorEl(event.currentTarget)}
                                sx={{
                                    width: 50,
                                    height: 50,
                                    minWidth: 50,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    border: "1px dashed #ccc",
                                    flexShrink: 0,
                                    borderRadius: 2,
                                }}
                            >
                                <Add sx={{ fontSize: 24, color: "#757575" }} />
                            </Box>

                            <Stack direction="row" spacing={1} alignItems="center" sx={{ overflowX: "auto", width: "100%", pb: 1 }}>
                                {localItem.resources.map((res, i) => {
                                    return (
                                        <Paper
                                            key={`${res}-resource-${i}`}
                                            sx={{
                                            width: 50,
                                            height: 50,
                                            minWidth: 50,
                                            overflow: "hidden",
                                            position: "relative",
                                            borderRadius: 1,
                                            backgroundColor: "#000",
                                            flexShrink: 0,
                                            }}
                                        >
                                            <img
                                                src={res}
                                                alt={`resource-${i}`}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />

                                            <IconButton
                                                size="small"
                                                sx={{
                                                    position: "absolute",
                                                    top: 1,
                                                    right: 0.5,
                                                    backgroundColor: "rgba(0,0,0,0.3)",
                                                    color: "#fff",
                                                    padding: "2px",
                                                    "&:hover": {
                                                    backgroundColor: "rgba(0,0,0,0.5)",
                                                    },
                                                }}
                                                onClick={() => {
                                                    handleChange("resources", localItem.resources.filter((r) => r !== res));
                                                }}
                                            >
                                                <Cancel fontSize="small" />
                                            </IconButton>
                                        </Paper>
                                    );
                                })}
                            </Stack>

                            <ResourcesView
                                id={localItem.id}
                                resourceAnchorEl={resourceAnchorEl}
                                setResourceAnchorEl={setResourceAnchorEl}
                                onselect={(res: string) => handleChange("resources", [...localItem.resources, res])}
                            />
                        </>
                    )}

        
                    <Box>
                        <Stack spacing={1}>
                            {(localItem.options || []).map((opt, i) => (
                                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <OptionIcon disabled />
                                    <TextField
                                        variant="standard"
                                        value={opt}
                                        onChange={(e) => handleOptionChange(e.target.value, i)}
                                        fullWidth
                                        sx={{ maxWidth: { xs: "100%", md: "50%" } }}
                                    />
                                    <IconButton onClick={() => removeOption(i)}>
                                        <Delete />
                                    </IconButton>
                                </Box>
                            ))}
                            <Box
                                onClick={addOption}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    opacity: 0.6,
                                    cursor: "pointer",
                                    "&:hover": { opacity: 0.8 },
                                }}
                            >
                                <OptionIcon disabled />
                                <Typography variant="body2">Add Option</Typography>
                            </Box>
                        </Stack>
                    </Box>
        
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box sx={{ width: 24, height: 24 }}>
                            {auto_marker ? (
                                <img
                                    src={StarIcon}
                                    alt="AutoMarker"
                                    style={{ width: 24, height: 24, cursor: 'pointer' }}
                                    onClick={handleImageClick}
                                />
                            ) : null}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2">Required</Typography>
                            <Switch checked={!!localItem.required} onChange={toggleRequired} />

                            <IconButton onClick={handleMenuOpen} sx={{ marginLeft: 1 }}>
                                <MoreVert />
                            </IconButton>

                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                                <MenuItem onClick={toggleResourcesVisibility}>
                                    {resourcesVisible ? "Hide resources" : "Show resources"}
                                </MenuItem>
                                <MenuItem onClick={toggleDescriptionVisibility}>
                                    {descriptionVisible ? "Hide description" : "Show description"}
                                </MenuItem>
                                
                                <MenuItem onClick={toggleShuffleOptions}>
                                    {localItem.shuffle_options ? "Disable Options Shuffling" : "Enable Options Shuffling"}
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                </>
            ) : (
            <>
                <TextField
                    variant="standard"
                    value={localItem.marks}
                    onChange={(e) => handleChange("marks", parseInt(e.target.value))}
                    label="Marks"
                    type="number"
                    sx={{ maxWidth: "50%" }}
                />
    
                <Box sx={{ mb: 2 }}>
                
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                            Select Correct Answer(s)
                        </Typography>

                        {(localItem.options || []).map((opt, i) => (
                            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <OptionIcon 
                                    checked={isOptionCorrect(opt)}
                                    onChange={() => handleCorrectAnswerSelect(opt)}
                                    sx={{ cursor: 'pointer' }}
                                />
                                <Typography>
                                    {opt}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
    
                <Button
                    variant="contained"
                    onClick={handleDoneClick}
                    sx={{
                        alignSelf: "flex-end",
                        marginTop: 2,
                        paddingLeft: 6,
                        paddingRight: 6,
                        backgroundColor: "#53826b",
                    }}
                >
                    Done
                </Button>
            </>
            )}
      </Paper>
    );
}

export default memo(ObjectiveView);